import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import MetricsSection from "./MetricsSection";

const EnvironmentalMetrics: React.FC<{
  aggMeasureList?: any[],
  dashboardDetailPage?: (data: any) => void,
  variant?: any,
  categoryList: any
}> = ({
  aggMeasureList = [],
  dashboardDetailPage,
  variant,
  categoryList
}) => {
    // Create a safe fallback if dashboardDetailPage is undefined.
    const safeDashboardDetailPage = dashboardDetailPage ?? (() => { });

    const colorIndexData: any = ["#000000", "#871014", "#FFEF5C"];

    const BiosSubstance = Array.isArray(aggMeasureList) ? aggMeasureList.filter(
      (val) =>
        !colorIndexData.includes(val?.color) &&
        val?.group === "bio" &&
        val?.group !== val?.substanceKey
    ).sort((c1, c2) => c1?.order_no - c2?.order_no) : [];

    const GasesSubstance = Array.isArray(aggMeasureList) ? aggMeasureList.filter(
      (val) =>
        !colorIndexData.includes(val?.color) &&
        val?.group === "gas" &&
        val?.group !== val?.substanceKey
    ).sort((c1, c2) => c1?.order_no - c2?.order_no) : [];

    const ChemicalsSubstance = Array.isArray(aggMeasureList) ? aggMeasureList.filter(
      (val) =>
        !colorIndexData.includes(val?.color) &&
        val?.group === "chems" &&
        val?.group !== val?.substanceKey
    ).sort((c1, c2) => c1?.order_no - c2?.order_no) : [];

    const wthrTemperature = Array.isArray(aggMeasureList)
      ? aggMeasureList
        .filter((val) => {
          const indexNumber = Number(val.index);
          return (
            // Condition 1: indexes 2, 4, or 5 and substanceKey is not "pm10" or "pm2dot5"
            ([2, 4, 5].includes(indexNumber) &&
              val.substanceKey !== "pm10" &&
              val.substanceKey !== "pm2dot5") ||
            // Condition 2: "count" variant with index >= 2 and substanceKey is "pcc10" or "pcc2dot5"
            (variant === "count" &&
              indexNumber >= 2 &&
              (val.substanceKey === "pcc10" || val.substanceKey === "pcc2dot5")) ||
            // Condition 3: non-"count" variant with index >= 2 and substanceKey is "pm10" or "pm2dot5"
            (variant !== "count" &&
              indexNumber >= 2 &&
              (val.substanceKey === "pm10" || val.substanceKey === "pm2dot5"))
          );
        })
        .sort((a, b) => Number(a.index) - Number(b.index))
      : [];


    const WeatherSubstances = Array.isArray(aggMeasureList) ? aggMeasureList.filter((val) => {
      if (colorIndexData.includes(val?.color)) return false;
      return (
        (val?.group === "wthr" && ["temperature", "humidity"].includes(val?.substanceKey)) ||
        (val?.group === "particulate" && val?.substanceKey === "isopm10psd") ||
        val?.group === "thi" ||
        (val?.group === "voc" && val?.color !== "#871014")
      );
    }) : [];

    const particulatesMetrics = Array.isArray(aggMeasureList) ? aggMeasureList.filter((val) => (
      (!colorIndexData.includes(val?.color) &&
        val?.group === "particulate" &&
        val?.group !== val?.substanceKey &&
        val?.substanceKey !== "isopm10psd" &&
        variant === "count" &&
        (val?.index === 0 || val?.index === 1) &&
        ["pcc10", "pcc2dot5"].includes(val?.substanceKey)) ||
      (variant !== "count" &&
        (val?.index === 0 || val?.index === 1) &&
        ["pm10", "pm2dot5"].includes(val?.substanceKey))
    )) : [];

    const elevatedTiles =
      (aggMeasureList.length &&
        aggMeasureList
          .filter(
            (val: any) =>
              [2, 4, 5].includes(val?.index) &&
              val.group !== val.substanceKey &&
              (variant === "count" ? !["pm10", "pm2dot5"].includes(val.substanceKey)
                : !["pcc10", "pcc2dot5"].includes(val.substanceKey))
          )) || [];

    const otherTiles =
      (aggMeasureList.length &&
        aggMeasureList
          .filter(
            (val: any) =>
              !val.category_id && ![2, 4, 5].includes(val?.index) &&
              val.group !== val.substanceKey || (val.group === 'thi') &&
              (variant === "count" ? !["pm10", "pm2dot5"].includes(val.substanceKey)
                : !["pcc10", "pcc2dot5"].includes(val.substanceKey))

          )) || [];

    return (
      <Box sx={{ p: 2, width: "100%", mx: "auto" }}>
        {(Array.isArray(aggMeasureList) && aggMeasureList.length > 0) ? (
          <>
            {elevatedTiles.length > 0 &&
              <MetricsSection title={"Elevated Thresholds"} metrics={elevatedTiles} summaryPage={safeDashboardDetailPage} />
            }
            {otherTiles.length > 0 &&
              <MetricsSection title={""} metrics={otherTiles} summaryPage={safeDashboardDetailPage} />
            }
            {categoryList.length > 0 && categoryList.map((item: any) => {
              const tileData =
                (aggMeasureList.length &&
                  aggMeasureList
                    .filter(
                      (val: any) =>
                        val.category_id == item.sk && ![2, 4, 5].includes(val?.index) &&
                        (val.group !== val.substanceKey || val.substanceKey !== "pm2dot5psd") &&

                        (variant === "count" ? !["pm10", "pm2dot5", "pm_biop", "pm_bio", "pm_bioi"].includes(val.substanceKey)
                          : !["pcc10", "pcc2dot5", "pc_biop", "pc_bio", "pc_bioi"].includes(val.substanceKey))

                    ).sort((c1: any, c2: any) => c1.order_no - c2.order_no)) || [];
              return tileData.length > 0 ? (
                <MetricsSection
                  key={item.sk}
                  title={item.category_name}
                  metrics={tileData}
                  summaryPage={safeDashboardDetailPage}
                />
              ) : null;
            })}
            {/* <MetricsSection title="Elevated Thresholds" metrics={wthrTemperature} summaryPage={safeDashboardDetailPage} />
          <MetricsSection title="" metrics={WeatherSubstances} summaryPage={safeDashboardDetailPage} />
          {particulatesMetrics.length > 0 && (
            <MetricsSection title="Particulates" metrics={particulatesMetrics} summaryPage={safeDashboardDetailPage} />
          )}
          <MetricsSection title="Bio" metrics={BiosSubstance} summaryPage={safeDashboardDetailPage} />
          <MetricsSection title="Gases" metrics={GasesSubstance} summaryPage={safeDashboardDetailPage} />
          <MetricsSection title="Chemicals" metrics={ChemicalsSubstance} summaryPage={safeDashboardDetailPage} /> */}
          </>
        ) : (
          <Box px={4}>
            <Grid container spacing={2} mt={2}>
              <Grid container sx={{ display: "block" }}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={2}
                  px={0}
                  sx={{ maxWidth: "100% !important" }}
                >
                  <Typography
                    fontSize={15}
                    textAlign="center"
                    sx={{
                      border: "1px solid #dfdfdf",
                      padding: 2,
                      borderRadius: 2,
                    }}
                  >
                    We're currently unable to retrieve the data you requested.
                    <br /> This could be due to the device being offline or
                    the readings not being available at the moment.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

export default EnvironmentalMetrics;
